import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';

import { translate } from 'services/i18nService';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import { TOKEN_RANGE } from 'enum/helpers';
import { chatFontSettings } from 'utils/helpers';

import coinImageTier1 from '../../../images/chat/single-coin.png';
import coinImageTier2 from '../../../images/chat/small-hand.png';
import coinImageTier3 from '../../../images/chat/big-hand.png';
import tokensNotif from '../../../images/mobileNotificationsChat/tokensNotif.png';
import nnLogo from '../../../images/logo/nnlogo.png';
import temptedLogo from '../../../images/logo/temptedlogo.png';

import styles from './ChatNotificationsTypes.css';


function TippingMobileNotification({
  chatSettings,
  isBroadcasterChat,
  isDesktop,
  message,
}) {
  const { profileName } = ProfileStore;
  const { user: { username } } = UserStore;

  const parsedTokenValue = parseInt(message.notificationExtra, 10);
  const oneToken = parsedTokenValue === 1;

  const tierOneTokens = parsedTokenValue < TOKEN_RANGE.LOWEST_SOUND_TOKEN_AMOUNT;
  const tierTwoTokens = (
    parsedTokenValue >= TOKEN_RANGE.LOWEST_SOUND_TOKEN_AMOUNT &&
    parsedTokenValue < TOKEN_RANGE.LOW_SOUND_TOKEN_AMOUNT
  );
  const tierThreeTokens = (
    parsedTokenValue >= TOKEN_RANGE.LOW_SOUND_TOKEN_AMOUNT &&
    parsedTokenValue < TOKEN_RANGE.MIDDLE_SOUND_TOKEN_AMOUNT
  );
  const tierFourTokens = (
    parsedTokenValue >= TOKEN_RANGE.MIDDLE_SOUND_TOKEN_AMOUNT &&
    parsedTokenValue < TOKEN_RANGE.HIGH_SOUND_TOKEN_AMOUNT
  );
  const tierFiveTokens = parsedTokenValue >= TOKEN_RANGE.HIGH_SOUND_TOKEN_AMOUNT;

  const isAnonimous = parseInt(message.anonymous, 10) === 1;

  const handleLifeTouchTippingText = () => {
    switch (true) {
      case oneToken:
        return translate('v2profile.livetouch.poked.one.token', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong>${isBroadcasterChat ? username : profileName}</strong>`,
          2: `<strong>${translate('v2profile.tipping.token.singular')}</strong>`,
        });

      case tierOneTokens:
        return translate('v2profile.livetouch.poked.multiple.tokens', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong>${isBroadcasterChat ? username : profileName}</strong>`,
          2: `<strong>${message.notificationExtra}</strong>`,
          3: `<strong>${translate('v2profile.tipping.token.plural')}</strong>`,
        });

      case tierTwoTokens:
        return translate('v2profile.livetouch.touch.tokens', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong>${isBroadcasterChat ? username : profileName}</strong>`,
          2: `<strong>${message.notificationExtra}</strong>`,
          3: `<strong>${translate('v2profile.tipping.token.plural')}</strong>`,
        });

      case tierThreeTokens:
        return translate('v2profile.livetouch.touch.tokens', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong>${isBroadcasterChat ? username : profileName}</strong>`,
          2: `<strong><span class='${styles.amountOfTokens}'>${message.notificationExtra}</strong>`,
          3: `<strong><span class='${styles.amountOfTokens}'>${translate(
            'v2profile.tipping.token.plural'
          )}</span></strong>`,
        });

      case tierFourTokens:
        return translate('v2profile.livetouch.feel.tokens', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong>${profileName}</strong>`,
          2: `<strong><span class='${styles.amountOfTokens}'>${message.notificationExtra}</span></strong>`,
          3: `<strong><span class='${styles.amountOfTokens}'>${translate(
            'v2profile.tipping.token.plural'
          )}</span></strong>`,
        });

      case tierFiveTokens:
        return translate('v2profile.livetouch.fuck.tokens', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong>${profileName}</strong>`,
          2: `<strong><span class='${styles.amountOfTokens}'>${message.notificationExtra}</span></strong>`,
          3: `<strong><span class='${styles.amountOfTokens}'>${translate(
            'v2profile.tipping.token.plural'
          )}</span></strong>`,
        });

      default:
        return '';
    }
  };

  const tokenAmountClass = classNames({
    [styles.amountOfTokensAnim]: tierFiveTokens,
    [styles.amountOfTokensAnim]: tierFourTokens,
    [styles.amountOfTokensBold]: tierThreeTokens,
  });

  const handleTippingText = () => {
    const currency = message?.currency && `<strong>${message?.currency}</strong>`;
    switch (true) {
      case oneToken:
        return translate('v2profile.tipping.one.token', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: currency ? `<strong>1</strong> ${currency}` : (
            `<strong>${translate('v2profile.tipping.token.singular')}</strong>`),
        });

      default:
        return translate('v2profile.tipping.tokens.notif', {
          0: `<strong>${
            isAnonimous ? translate('tipping.an.anonymous.user') : message.senderOriginalUsername
          }</strong>`,
          1: `<strong><span class='${tokenAmountClass}'>${message.notificationExtra}</span></strong>`,
          2: currency ? currency : (
            `<strong><span class='${tokenAmountClass}'>${translate('v2profile.tipping.token.plural')}</span></strong>`),
        });
    }
  };

  const handleImageTokens = () => {
    switch (true) {
      case oneToken:
      case tierOneTokens:
        return coinImageTier1;

      case tierTwoTokens:
        return coinImageTier2;

      case tierThreeTokens:
        return coinImageTier3;

      default:
        return '';
    }
  };

  const handleExternalImg = useMemo(() => {
    switch (message?.tenant?.toLowerCase()) {
      case 'tempted':
        return (
          <Tooltip title="Tempted">
            <img
              alt="temptedLogo"
              className={styles.externalImg}
              src={temptedLogo}
            />
          </Tooltip>
        );

      case 'nakednews':
        return (
          <Tooltip title="Naked News">
            <img
              alt="nakedNews"
              className={styles.externalImg}
              src={nnLogo}
            />
          </Tooltip>
        );

      default:
        return null;
    }
  }, [message?.tenant]);

  const handleWrapperDesktopClass = () => {
    switch (true) {
      case tierThreeTokens:
        return !message.isHistoryMessage && styles.tierThreeAnimation;

      case tierFourTokens:
        return message.isHistoryMessage ? styles.tierFourWithoutAnimation : styles.tierFourAnimation;

      case tierFiveTokens:
        return message.isHistoryMessage ? styles.tierFiveWithoutAnimation : styles.tierFiveAnimation;

      default:
        return '';
    }
  };

  const coinImgClass = classNames({
    [styles.coinOne]: tierOneTokens,
    [styles.coinTwo]: tierTwoTokens,
    [styles.coinThree]: tierThreeTokens,
  });

  const notifTextClass = classNames({
    [styles.basicNotifText]: true,
    [styles.silverText]: isDesktop,
    [styles.yellowText]: !isDesktop,
    [styles.tierFiveText]: tierFiveTokens && isDesktop,
    [styles.amountOfTokensAnim]: tierFiveTokens && !isDesktop,
  });

  const wrapperClass = classNames({
    [styles.goldNotifWrapperDesktop]: isDesktop,
    [handleWrapperDesktopClass()]: isDesktop,
    [styles.goldNotifWrapper]: !isDesktop,
  });

  return (
    <Fragment>
      <div className={wrapperClass}>
        {!tierFourTokens && !tierFiveTokens && (
          <div className={styles.goldImage}>
            <img
              alt="coinImage"
              className={isDesktop ? coinImgClass : styles.coinOne}
              src={isDesktop ? handleImageTokens() : tokensNotif}
            />
          </div>
        )}
        {tierFourTokens && (
          <div className={styles.goldImage}>
            {!isDesktop && (
              <img
                alt="one coin"
                className={styles.coinOne}
                src={tokensNotif}
              />
            )}
          </div>
        )}
        {tierFiveTokens && !isDesktop && (
          <div className={styles.goldImage}>
            <img
              alt="one coin"
              className={styles.coinOne}
              src={tokensNotif}
            />
          </div>
        )}
        <div
          className={notifTextClass}
          style={chatFontSettings(isDesktop, chatSettings)}
        >
          {message.isLiveTouchOn ? handleLifeTouchTippingText() : handleTippingText()}
          {message.tenant && <span> - {handleExternalImg}</span>}
        </div>
      </div>
    </Fragment>
  );
}

TippingMobileNotification.propTypes = {
  chatSettings: PropTypes.object,
  isBroadcasterChat: PropTypes.bool,
  isDesktop: PropTypes.bool,
  message: PropTypes.object,
};

TippingMobileNotification.defaultProps = {
  isBroadcasterChat: false,
  chatSettings: {},
  isDesktop: false,
  message: {},
};

export default TippingMobileNotification;
